import {FC, useEffect, useMemo, useRef, useState} from 'react'
import QRCode from 'react-qr-code'
import {
  getLoggedOutProfiles,
  getWhatsappAccounts,
  addWhatsapp,
  reconnectWhatsapp,
  logoutWhatsapp,
  removeWhatsapp,
  setAutoSend,
  setSmsAutoSend,
  sendLoginLink,
  sendPhoto,
  sendPhotoBulk,
  showQr,
  getSmsStatus,
  sendSmsBulk,
  sendTestSms,
  sendTestWhatsapp,
  excludeAccount,
} from './core/_requests'
import {LoggedOutProfile, WhatsappAccount} from './core/_models'

const FB_ACCESS_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN || ''

// Prefer the live FB Graph picture (same pattern as the rest of the app), fall back
// to a stored URL, else null so the placeholder renders.
function picUrl(p: LoggedOutProfile): string {
  if (p.facebook_id && FB_ACCESS_TOKEN) {
    return `https://graph.facebook.com/${p.facebook_id}/picture?type=square&access_token=${FB_ACCESS_TOKEN}`
  }
  return p.profile_picture || ''
}

const previewUrl = (id: string) => `https://pp234234.fbzipper.com/preview/${id}/`

const DEFAULT_TEMPLATE =
  'לקוח יקר.\n' +
  'הפרופיל התנתק מהמערכת והפרסום האוטומטי הושהה זמנית\n' +
  'לחידוש הפרסום, יש ללחוץ על הקישור הבא ולאשר התחברות בלחיצת כפתור\n' +
  '{link}\n\n' +
  'אוהבים צוות FBZipper 🫶'

const TEMPLATE_KEY = 'logged_out_profiles_msg_template'
const SMS_TEMPLATE_KEY = 'logged_out_profiles_sms_template'
const DEFAULT_SMS_TEMPLATE =
  'לקוח יקר, הפרופיל התנתק והפרסום הושהה.\n' +
  'לחידוש הפרסום היכנסו לקישור ואשרו התחברות:\n' +
  '{link}\n' +
  'צוות FBZipper'
const ATTACH_IMG_KEY = 'logged_out_profiles_attach_image'
// Instructional image attached to the login message / sent by "send photo only".
// Mirrors LOGIN_HELP_IMAGE_URL on the backend (uploaded via scripts/upload-login-help-image.js).
const LOGIN_HELP_IMAGE_URL = 'https://zipper.ams3.cdn.digitaloceanspaces.com/zipper/login-help/confirm-login.jpg'

// Build the actual message for a profile: {link} -> its login link, {name} -> name.
// If {link} is missing the backend appends the link, but we keep the preview honest.
function buildMessage(template: string, p: {login_url: string; name: string}): string {
  let m = template.replace(/\{link\}/g, p.login_url).replace(/\{name\}/g, p.name)
  if (!m.includes(p.login_url)) m += '\n' + p.login_url
  return m
}

function relTime(iso: string | null): string {
  if (!iso) return 'מעולם לא'
  const ms = Date.now() - new Date(iso).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 1) return 'הרגע'
  if (min < 60) return `לפני ${min} ד׳`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `לפני ${hr} ש׳`
  const d = Math.floor(hr / 24)
  return `לפני ${d} י׳`
}

const LoggedOutProfilesPage: FC = () => {
  const [rows, setRows] = useState<LoggedOutProfile[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [phones, setPhones] = useState<Record<string, string>>({})
  const [sentFor, setSentFor] = useState<Record<string, string>>({})
  const [sending, setSending] = useState<Record<string, boolean>>({})
  const [revealed, setRevealed] = useState<Record<string, boolean>>({})
  const [qrBusy, setQrBusy] = useState<Record<string, boolean>>({})
  const [qrTried, setQrTried] = useState<Record<string, boolean>>({})
  const [template, setTemplate] = useState<string>(() => {
    try { return localStorage.getItem(TEMPLATE_KEY) || DEFAULT_TEMPLATE } catch { return DEFAULT_TEMPLATE }
  })
  const [showTemplate, setShowTemplate] = useState(false)
  const [smsTemplate, setSmsTemplate] = useState<string>(() => {
    try { return localStorage.getItem(SMS_TEMPLATE_KEY) || DEFAULT_SMS_TEMPLATE } catch { return DEFAULT_SMS_TEMPLATE }
  })
  const saveSmsTemplate = (t: string) => {
    setSmsTemplate(t)
    try { localStorage.setItem(SMS_TEMPLATE_KEY, t) } catch (_) {}
  }
  const [attachImage, setAttachImage] = useState<boolean>(() => {
    try { return localStorage.getItem(ATTACH_IMG_KEY) !== '0' } catch { return true }
  })
  const [photoBusy, setPhotoBusy] = useState<Record<string, boolean>>({})
  const [smsBusy, setSmsBusy] = useState<Record<string, boolean>>({})
  const [smsConfigured, setSmsConfigured] = useState(false)
  const [smsFrom, setSmsFrom] = useState('')
  const [bulkSmsBusy, setBulkSmsBusy] = useState(false)
  const [testPhone, setTestPhone] = useState('')
  const [testBusy, setTestBusy] = useState(false)
  const [testWaPhone, setTestWaPhone] = useState('')
  const [testWaBusy, setTestWaBusy] = useState(false)

  const saveTemplate = (t: string) => {
    setTemplate(t)
    try { localStorage.setItem(TEMPLATE_KEY, t) } catch (_) {}
  }
  const saveAttachImage = (v: boolean) => {
    setAttachImage(v)
    try { localStorage.setItem(ATTACH_IMG_KEY, v ? '1' : '0') } catch (_) {}
  }

  const [accounts, setAccounts] = useState<WhatsappAccount[]>([])
  const [autoSend, setAutoSendState] = useState(false)
  const [autoBusy, setAutoBusy] = useState(false)
  const autoBusyRef = useRef(false)
  const [smsAutoSend, setSmsAutoSendState] = useState(false)
  const [smsAutoBusy, setSmsAutoBusy] = useState(false)
  const smsAutoBusyRef = useRef(false)
  const [waBusy, setWaBusy] = useState<Record<string, boolean>>({})
  const waPoll = useRef<any>(null)

  const load = async () => {
    setLoading(true)
    try {
      const res = await getLoggedOutProfiles(query)
      const list = res.data?.profiles || []
      setRows(list)
      setTotal(res.data?.total || list.length)
      const ph: Record<string, string> = {}
      list.forEach((p) => (ph[p._id] = p.resolved_phone || ''))
      setPhones(ph)
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  // Reload on query (debounced).
  useEffect(() => {
    const t = setTimeout(load, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const refreshWa = async () => {
    try {
      const res = await getWhatsappAccounts()
      setAccounts(res.data?.accounts || [])
      if (!autoBusyRef.current) setAutoSendState(!!res.data?.auto_send)
      if (!smsAutoBusyRef.current) setSmsAutoSendState(!!res.data?.sms_auto_send)
    } catch (_) {}
  }

  const toggleAutoSend = async () => {
    const next = !autoSend
    autoBusyRef.current = true
    setAutoBusy(true)
    setAutoSendState(next) // optimistic
    try {
      await setAutoSend(next)
    } catch (_) {
      setAutoSendState(!next) // revert on failure
      alert('עדכון האוטומציה נכשל')
    }
    autoBusyRef.current = false
    setAutoBusy(false)
  }
  const toggleSmsAutoSend = async () => {
    const next = !smsAutoSend
    if (next && !smsConfigured) return alert('ספק ה‑SMS לא מוגדר')
    if (next && !window.confirm('להפעיל שליחת SMS אוטומטית? כל מי שהתנתק וטרם קיבל SMS היום יקבל קישור התחברות ב‑SMS (07:00–21:00).')) return
    smsAutoBusyRef.current = true
    setSmsAutoBusy(true)
    setSmsAutoSendState(next) // optimistic
    try {
      await setSmsAutoSend(next)
    } catch (_) {
      setSmsAutoSendState(!next) // revert on failure
      alert('עדכון אוטומציית ה‑SMS נכשל')
    }
    smsAutoBusyRef.current = false
    setSmsAutoBusy(false)
  }
  useEffect(() => {
    refreshWa()
    waPoll.current = setInterval(refreshWa, 3000)
    return () => clearInterval(waPoll.current)
  }, [])

  useEffect(() => {
    getSmsStatus().then((r) => {
      setSmsConfigured(!!r.data?.configured)
      setSmsFrom(r.data?.from || '')
    }).catch(() => {})
  }, [])

  // "Connected" for sending = at least one account is connected (round-robin pool).
  const connectedCount = accounts.filter((a) => a.status === 'connected').length
  const waConnected = connectedCount > 0

  const setBusy = (id: string, v: boolean) => setWaBusy((m) => ({...m, [id]: v}))

  const doAdd = async () => {
    setBusy('add', true)
    try {
      await addWhatsapp()
      await refreshWa()
    } catch (e: any) {
      alert('שירות הוואטסאפ אינו זמין כרגע')
    }
    setBusy('add', false)
  }
  const doReconnect = async (id: string) => {
    setBusy(id, true)
    try {
      await reconnectWhatsapp(id)
      await refreshWa()
    } catch (e: any) {
      alert('שירות הוואטסאפ אינו זמין כרגע')
    }
    setBusy(id, false)
  }
  const doLogout = async (id: string) => {
    if (!window.confirm('לנתק את מספר הוואטסאפ? יידרש לסרוק קוד חדש.')) return
    setBusy(id, true)
    try {
      await logoutWhatsapp(id)
      await refreshWa()
    } catch (_) {}
    setBusy(id, false)
  }
  const doRemove = async (id: string) => {
    if (!window.confirm('להסיר את החשבון לגמרי?')) return
    setBusy(id, true)
    try {
      await removeWhatsapp(id)
      await refreshWa()
    } catch (_) {}
    setBusy(id, false)
  }

  const send = async (p: LoggedOutProfile) => {
    const ph = (phones[p._id] || '').trim()
    if (!ph) return alert('הזן מספר טלפון')
    if (!waConnected) return alert('חבר קודם את הוואטסאפ (סרוק את הקוד למעלה)')
    setSending((m) => ({...m, [p._id]: true}))
    try {
      const res = await sendLoginLink(p._id, ph, buildMessage(template, p), attachImage)
      setSentFor((m) => ({...m, [p._id]: res.data?.sent_to || ph}))
    } catch (e: any) {
      alert('שליחה נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setSending((m) => ({...m, [p._id]: false}))
  }

  const sendSms = async (p: LoggedOutProfile) => {
    const ph = (phones[p._id] || '').trim()
    if (!ph) return alert('הזן מספר טלפון')
    if (!smsConfigured) return alert('ספק ה‑SMS לא מוגדר')
    setSmsBusy((m) => ({...m, [p._id]: true}))
    try {
      const res = await sendLoginLink(p._id, ph, buildMessage(smsTemplate, p), false, 'sms')
      setSentFor((m) => ({...m, [p._id]: res.data?.sent_to || ph}))
    } catch (e: any) {
      alert('שליחת SMS נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setSmsBusy((m) => ({...m, [p._id]: false}))
  }

  // Eligible for the bulk SMS nudge: every logged-out profile (active barcode or not),
  // not excluded, not already SMS'd today (Israel calendar day).
  const ilDay = (d: string | number) => new Date(d).toLocaleDateString('en-CA', {timeZone: 'Asia/Jerusalem'})
  const smsedToday = (iso: string | null) => !!iso && ilDay(iso) === ilDay(Date.now())
  const smsBulkIds = useMemo(
    () => rows.filter((r) => !r.excluded && !smsedToday(r.sms_sent_at)).map((r) => r._id),
    [rows]
  )
  const sendSmsToActiveBarcode = async () => {
    if (!smsConfigured) return alert('ספק ה‑SMS לא מוגדר')
    if (!smsBulkIds.length) return
    if (!window.confirm(`לשלוח SMS ל‑${smsBulkIds.length} פרופילים מנותקים? (כולל ללא ברקוד, ללא מוחרגים ומי שכבר קיבל SMS היום. לא ימנע וואטסאפ)`)) return
    setBulkSmsBusy(true)
    try {
      const res = await sendSmsBulk(smsBulkIds, smsTemplate)
      alert(`SMS נשלחו: ${res.data?.sent || 0} / ${res.data?.total || smsBulkIds.length} (נכשלו: ${res.data?.failed || 0}${(res.data as any)?.skipped ? `, דולגו: ${(res.data as any).skipped}` : ''})`)
      await load()
    } catch (e: any) {
      alert('שליחת SMS נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setBulkSmsBusy(false)
  }

  const fireTestWhatsapp = async () => {
    if (!testWaPhone.trim()) return alert('הזן מספר לבדיקה')
    if (!waConnected) return alert('חבר קודם את הוואטסאפ')
    setTestWaBusy(true)
    try {
      const preview = buildMessage(template, {login_url: 'https://login.fbzipper.com/api/login-url/test', name: 'בדיקה'})
      const res = await sendTestWhatsapp(testWaPhone.trim(), preview, attachImage)
      alert('נשלח ל‑' + (res.data?.sent_to || testWaPhone))
    } catch (e: any) {
      alert('שליחת בדיקה נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setTestWaBusy(false)
  }

  const fireTestSms = async () => {
    if (!testPhone.trim()) return alert('הזן מספר לבדיקה')
    setTestBusy(true)
    try {
      // Send the actual SMS template (with a sample link/name) so the test reflects it.
      const preview = buildMessage(smsTemplate, {login_url: 'https://login.fbzipper.com/api/login-url/test', name: 'בדיקה'})
      const res = await sendTestSms(testPhone.trim(), preview)
      alert('נשלח ל‑' + (res.data?.sent_to || testPhone))
    } catch (e: any) {
      alert('שליחת בדיקה נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setTestBusy(false)
  }

  const sendPhotoOnly = async (p: LoggedOutProfile) => {
    const ph = (phones[p._id] || '').trim()
    if (!ph) return alert('הזן מספר טלפון')
    if (!waConnected) return alert('חבר קודם את הוואטסאפ (סרוק את הקוד למעלה)')
    setPhotoBusy((m) => ({...m, [p._id]: true}))
    try {
      await sendPhoto(p._id, ph)
      setSentFor((m) => ({...m, [p._id]: ph}))
    } catch (e: any) {
      alert('שליחת התמונה נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setPhotoBusy((m) => ({...m, [p._id]: false}))
  }

  const tryGetQr = async (p: LoggedOutProfile) => {
    setQrBusy((m) => ({...m, [p._id]: true}))
    try {
      await showQr(p._id)
      setQrTried((m) => ({...m, [p._id]: true}))
      // The QR lands ~15-30s later; refresh the list then so the badge flips.
      setTimeout(() => load(), 25000)
    } catch (e: any) {
      alert('הפעלת קוד נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setQrBusy((m) => ({...m, [p._id]: false}))
  }

  const [bulkPhotoBusy, setBulkPhotoBusy] = useState(false)
  // Profiles already messaged a login link but not yet sent the image ("the missed ones").
  const missedPhotoIds = useMemo(
    () => rows.filter((r) => r.last_message && !r.photo_sent).map((r) => r._id),
    [rows]
  )
  const sendPhotoToMissed = async () => {
    if (!waConnected) return alert('חבר קודם את הוואטסאפ')
    if (!missedPhotoIds.length) return
    if (!window.confirm(`לשלוח את תמונת ההסבר ל‑${missedPhotoIds.length} פרופילים שקיבלו קישור אך לא תמונה?`)) return
    setBulkPhotoBusy(true)
    try {
      const res = await sendPhotoBulk(missedPhotoIds)
      alert(`נשלחו ${res.data?.sent || 0} / ${res.data?.total || missedPhotoIds.length} (נכשלו: ${res.data?.failed || 0})`)
      await load()
    } catch (e: any) {
      alert('שליחה נכשלה: ' + (e?.response?.data?.error || e?.message))
    }
    setBulkPhotoBusy(false)
  }

  const toggleExclude = async (p: LoggedOutProfile) => {
    if (!p.account_id) return
    const next = !p.excluded
    if (next && !window.confirm(`להחריג את החשבון "${p.account_name}"? לא יישלחו אליו הודעות בשליחה מרוכזת/אוטומטית.`)) return
    // optimistic: mark all rows of this account
    setRows((rs) => rs.map((r) => (r.account_id === p.account_id ? {...r, excluded: next} : r)))
    try {
      await excludeAccount(p.account_id, next)
    } catch (e: any) {
      setRows((rs) => rs.map((r) => (r.account_id === p.account_id ? {...r, excluded: !next} : r)))
      alert('עדכון ההחרגה נכשל')
    }
  }

  const copy = (txt: string) => {
    try {
      navigator.clipboard.writeText(txt)
    } catch (_) {}
  }

  const counts = useMemo(() => ({active: rows.filter((r) => r.barcode_active).length}), [rows])

  return (
    <div style={{padding: '20px', maxWidth: 1400, margin: '0 auto'}} dir='rtl'>
      <h1 style={{fontSize: 22, fontWeight: 700, marginBottom: 4}}>פרופילים מנותקים</h1>
      <p style={{color: '#6b7280', fontSize: 13, marginBottom: 16}}>
        פרופילים של חשבונות פעילים ללא בדיקת תקינות ב‑5 הדקות האחרונות, ממוינים מהוותיק ביותר.
      </p>

      {/* Channels */}
      <div style={{display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20}}>
        {/* WhatsApp — multiple sender numbers, login links rotate round-robin across them */}
        <div style={{flex: '1 1 100%', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden'}}>
          <div style={{padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderBottom: '1px solid #f3f4f6'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
              <i className='bi bi-whatsapp' style={{fontSize: 20, color: '#25D366'}}></i>
              <div>
                <div style={{fontSize: 15, fontWeight: 600}}>מספרי וואטסאפ לשליחת קישורים</div>
                <div style={{fontSize: 12, color: '#9ca3af'}}>הקישורים מתחלקים בסבב בין כל המספרים המחוברים</div>
              </div>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: 14}}>
              {/* 5-min auto-send automation: each connected number sends one link to a
                  profile with an active barcode that wasn't WhatsApp'd today (07:00–21:00). */}
              <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none'}} title='כל 5 דקות כל מספר מחובר שולח קישור לפרופיל אחד עם ברקוד פעיל שלא נשלח אליו היום (07:00–21:00)'>
                <span style={{fontSize: 13, fontWeight: 600, color: autoSend ? '#16a34a' : '#6b7280'}}>
                  שליחה אוטומטית (כל 5 ד׳)
                </span>
                <div
                  onClick={(e) => {e.preventDefault(); if (!autoBusy) toggleAutoSend()}}
                  style={{
                    width: 44, height: 24, borderRadius: 999, position: 'relative', transition: 'background .15s',
                    background: autoSend ? '#16a34a' : '#d1d5db', opacity: autoBusy ? 0.6 : 1, cursor: autoBusy ? 'default' : 'pointer',
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 2, left: autoSend ? 2 : 22, width: 20, height: 20, borderRadius: '50%',
                    background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                  }} />
                </div>
              </label>
              <span style={{
                fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
                background: waConnected ? '#dcfce7' : '#f3f4f6',
                color: waConnected ? '#16a34a' : '#6b7280',
              }}>
                {waConnected ? `● ${connectedCount} מחוברים` : '○ אין מספר מחובר'}
              </span>
            </div>
          </div>
          <div style={{padding: 16, display: 'flex', gap: 14, flexWrap: 'wrap'}}>
            {accounts.map((a) => {
              const busy = !!waBusy[a.id]
              const connected = a.status === 'connected'
              return (
                <div key={a.id} style={{flex: '0 0 250px', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                  <div style={{padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, borderBottom: '1px solid #f3f4f6'}}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                      background: connected ? '#dcfce7' : a.status === 'qr' ? '#fef9c3' : '#f3f4f6',
                      color: connected ? '#16a34a' : a.status === 'qr' ? '#a16207' : '#6b7280',
                    }}>
                      {connected ? '● מחובר' : a.status === 'qr' ? '○ ממתין לסריקה' : a.status === 'logged_out' ? '○ מנותק' : '○ מתחבר...'}
                    </span>
                    <button className='btn btn-sm btn-icon btn-light-danger' title='הסר חשבון' disabled={busy} onClick={() => doRemove(a.id)} style={{width: 26, height: 26}}>
                      <i className='bi bi-trash' style={{fontSize: 12}} />
                    </button>
                  </div>
                  <div style={{padding: 14, textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10}}>
                    {connected ? (
                      <>
                        <i className='bi bi-check-circle-fill' style={{fontSize: 26, color: '#16a34a'}} />
                        <div style={{fontSize: 13, fontWeight: 600, color: '#16a34a'}} dir='ltr'>{a.number || ''}</div>
                        <button className='btn btn-sm btn-light-danger' disabled={busy} onClick={() => doLogout(a.id)}>
                          <i className='bi bi-box-arrow-right me-1' /> ניתוק
                        </button>
                      </>
                    ) : a.status === 'qr' && a.qr ? (
                      <>
                        <div style={{background: '#fff', padding: 10, borderRadius: 10, border: '1px solid #e5e7eb'}}>
                          <QRCode value={a.qr} size={150} fgColor='#1c1e21' bgColor='#ffffff' />
                        </div>
                        <p style={{margin: 0, fontSize: 11, color: '#6b7280', lineHeight: 1.5}}>
                          וואטסאפ ← מכשירים מקושרים ← קישור מכשיר
                        </p>
                        <button className='btn btn-link btn-sm p-0' disabled={busy} onClick={() => doReconnect(a.id)}>
                          <i className='bi bi-arrow-clockwise me-1' />צור קוד חדש
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{fontSize: 12, color: '#9ca3af'}}>
                          {a.status === 'logged_out' ? 'המספר נותק' : 'מתחבר לוואטסאפ...'}
                        </span>
                        <button className='btn btn-sm btn-success' disabled={busy} onClick={() => doReconnect(a.id)}>
                          <i className='bi bi-whatsapp me-1' /> {busy ? 'מתחבר…' : 'התחבר'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
            {/* Add a new sender number */}
            <button
              onClick={doAdd}
              disabled={!!waBusy['add']}
              style={{flex: '0 0 250px', minHeight: 210, border: '2px dashed #d1d5db', borderRadius: 12, background: '#fafafa', color: '#16a34a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer'}}
            >
              <i className='bi bi-plus-circle' style={{fontSize: 30}} />
              <span style={{fontSize: 13, fontWeight: 600}}>{waBusy['add'] ? 'יוצר קוד…' : 'הוסף מספר וואטסאפ'}</span>
            </button>
          </div>
          {/* Test WhatsApp (message + image) */}
          <div style={{padding: '12px 16px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
            <span style={{fontSize: 13, color: '#6b7280'}}>בדיקת וואטסאפ (הודעה + תמונה):</span>
            <input
              className='form-control form-control-sm'
              dir='ltr'
              style={{maxWidth: 160}}
              placeholder='05X-XXXXXXX'
              value={testWaPhone}
              onChange={(e) => setTestWaPhone(e.target.value)}
            />
            <button className='btn btn-sm btn-light-success' disabled={testWaBusy || !testWaPhone.trim() || !waConnected} onClick={fireTestWhatsapp}>
              {testWaBusy ? <span className='spinner-border spinner-border-sm' /> : <><i className='bi bi-whatsapp me-1' />שלח בדיקה</>}
            </button>
            {!waConnected ? <span style={{fontSize: 11, color: '#9ca3af'}}>חבר מספר תחילה</span> : null}
          </div>
        </div>

        {/* SMS (Multisend) */}
        <div style={{flex: '1 1 340px', background: '#fff', borderRadius: 12, border: `1px ${smsConfigured ? 'solid #e5e7eb' : 'dashed #d1d5db'}`, overflow: 'hidden'}}>
          <div style={{padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
              <i className='bi bi-chat-dots' style={{fontSize: 20, color: smsConfigured ? '#3b82f6' : '#9ca3af'}}></i>
              <div>
                <div style={{fontSize: 15, fontWeight: 600, color: smsConfigured ? '#1f2937' : '#6b7280'}}>SMS (Multisend)</div>
                <div style={{fontSize: 12, color: '#9ca3af'}}>שליחת קישור התחברות ב‑SMS</div>
              </div>
            </div>
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
              background: smsConfigured ? '#dcfce7' : '#f3f4f6', color: smsConfigured ? '#16a34a' : '#9ca3af',
            }}>{smsConfigured ? '● מחובר' : '○ לא מוגדר'}</span>
          </div>
          <div style={{padding: 18, textAlign: 'center', color: smsConfigured ? '#16a34a' : '#9ca3af', fontSize: 13}}>
            {smsConfigured ? (
              <>
                <div style={{marginBottom: 10}}>מוכן לשליחה{smsFrom ? ` · שולח: ${smsFrom}` : ''}</div>
                {/* Auto SMS batch: every few minutes, SMS every logged-out profile that hasn't
                    gotten one today (active barcode or not), 07:00–21:00 Israel time. */}
                <label
                  style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 12}}
                  title='שליחת SMS אוטומטית לכל הפרופילים המנותקים שטרם קיבלו SMS היום (כולל ללא ברקוד), בין 07:00 ל‑21:00'
                >
                  <span style={{fontSize: 13, fontWeight: 600, color: smsAutoSend ? '#16a34a' : '#6b7280'}}>
                    SMS אוטומטי לכולם (07:00–21:00)
                  </span>
                  <div
                    onClick={(e) => {e.preventDefault(); if (!smsAutoBusy) toggleSmsAutoSend()}}
                    style={{
                      width: 44, height: 24, borderRadius: 999, position: 'relative', transition: 'background .15s',
                      background: smsAutoSend ? '#16a34a' : '#d1d5db', opacity: smsAutoBusy ? 0.6 : 1, cursor: smsAutoBusy ? 'default' : 'pointer',
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 2, left: smsAutoSend ? 2 : 22, width: 20, height: 20, borderRadius: '50%',
                      background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                    }} />
                  </div>
                </label>
                <div style={{display: 'flex', gap: 6, justifyContent: 'center'}}>
                  <input
                    className='form-control form-control-sm'
                    dir='ltr'
                    style={{maxWidth: 160}}
                    placeholder='05X-XXXXXXX'
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                  />
                  <button className='btn btn-sm btn-light-info' disabled={testBusy || !testPhone.trim()} onClick={fireTestSms}>
                    {testBusy ? <span className='spinner-border spinner-border-sm' /> : 'בדיקת SMS'}
                  </button>
                </div>
                <button
                  className='btn btn-link btn-sm p-0 mt-2'
                  onClick={() => { setShowTemplate(true); setTimeout(() => document.getElementById('sms-template-editor')?.scrollIntoView({behavior: 'smooth', block: 'center'}), 50) }}
                >
                  <i className='bi bi-pencil-square me-1' />עריכת תבנית ה‑SMS
                </button>
              </>
            ) : 'לא מוגדר — הוסיפו פרטי חשבון Multisend ב‑.env'}
          </div>
        </div>
      </div>

      {/* Message template */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 16, overflow: 'hidden'}}>
        <div
          style={{padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer'}}
          onClick={() => setShowTemplate((v) => !v)}
        >
          <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <i className='bi bi-chat-square-text' style={{fontSize: 18, color: '#3b82f6'}}></i>
            <div>
              <div style={{fontSize: 14, fontWeight: 600}}>תבניות ההודעה (וואטסאפ + SMS)</div>
              <div style={{fontSize: 12, color: '#9ca3af'}}>{'{link} יוחלף בקישור ההתחברות של כל פרופיל, {name} בשם.'}</div>
            </div>
          </div>
          <i className={`bi ${showTemplate ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{color: '#9ca3af'}} />
        </div>
        {showTemplate ? (
          <div style={{padding: '0 16px 16px'}}>
            <div style={{fontSize: 13, fontWeight: 600, margin: '4px 0 6px'}}><i className='bi bi-whatsapp text-success me-1' />תבנית וואטסאפ</div>
            <textarea
              className='form-control'
              dir='rtl'
              rows={7}
              value={template}
              onChange={(e) => saveTemplate(e.target.value)}
              style={{fontSize: 13, lineHeight: 1.7}}
            />
            <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap'}}>
              <button className='btn btn-sm btn-light' onClick={() => saveTemplate(DEFAULT_TEMPLATE)}>
                <i className='bi bi-arrow-counterclockwise me-1' />איפוס לברירת מחדל
              </button>
              <span style={{fontSize: 11, color: '#9ca3af'}}>{'משתנים זמינים: {link}, {name}'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6'}}>
              <a href={LOGIN_HELP_IMAGE_URL} target='_blank' rel='noreferrer' title='פתח תמונה'>
                <img src={LOGIN_HELP_IMAGE_URL} alt='הסבר' style={{width: 46, height: 46, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb'}} />
              </a>
              <label style={{display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', margin: 0}}>
                <input type='checkbox' checked={attachImage} onChange={(e) => saveAttachImage(e.target.checked)} />
                צרף תמונת הסבר ("איך לאשר") להודעת ההתחברות
              </label>
            </div>
            <div style={{marginTop: 12}}>
              <div style={{fontSize: 12, color: '#6b7280', marginBottom: 4}}>תצוגה מקדימה{rows[0] ? ` (${rows[0].name})` : ''}:</div>
              <pre style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f0fdf4', border: '1px solid #bbf7d0',
                borderRadius: 10, padding: 12, fontSize: 13, fontFamily: 'inherit', margin: 0, direction: 'rtl',
              }}>
                {buildMessage(template, rows[0] || {login_url: 'https://login.fbzipper.com/api/login-url/…', name: 'שם הפרופיל'})}
              </pre>
            </div>

            {/* SMS template */}
            <div id='sms-template-editor' style={{fontSize: 13, fontWeight: 600, margin: '20px 0 6px', paddingTop: 16, borderTop: '1px solid #f3f4f6'}}>
              <i className='bi bi-chat-dots text-info me-1' />תבנית SMS
            </div>
            <textarea
              className='form-control'
              dir='rtl'
              rows={5}
              value={smsTemplate}
              onChange={(e) => saveSmsTemplate(e.target.value)}
              style={{fontSize: 13, lineHeight: 1.7}}
            />
            <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap'}}>
              <button className='btn btn-sm btn-light' onClick={() => saveSmsTemplate(DEFAULT_SMS_TEMPLATE)}>
                <i className='bi bi-arrow-counterclockwise me-1' />איפוס לברירת מחדל
              </button>
              <span style={{fontSize: 11, color: '#9ca3af'}}>{'משתנים זמינים: {link}, {name} · ב‑SMS אין תמונה'}</span>
            </div>
            <div style={{marginTop: 12}}>
              <div style={{fontSize: 12, color: '#6b7280', marginBottom: 4}}>תצוגה מקדימה{rows[0] ? ` (${rows[0].name})` : ''}:</div>
              <pre style={{
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#eff6ff', border: '1px solid #bfdbfe',
                borderRadius: 10, padding: 12, fontSize: 13, fontFamily: 'inherit', margin: 0, direction: 'rtl',
              }}>
                {buildMessage(smsTemplate, rows[0] || {login_url: 'https://login.fbzipper.com/api/login-url/…', name: 'שם הפרופיל'})}
              </pre>
            </div>
          </div>
        ) : null}
      </div>

      {/* Toolbar */}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap'}}>
        <div style={{fontSize: 13, color: '#6b7280'}}>
          {total} פרופילים מנותקים · {counts.active} עם ברקוד פעיל
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
          {smsConfigured && smsBulkIds.length > 0 ? (
            <button
              className='btn btn-sm btn-light-info'
              disabled={bulkSmsBusy}
              title='שלח SMS לכל הפרופילים המנותקים שטרם קיבלו SMS היום (כולל ללא ברקוד, לא מונע שליחת וואטסאפ)'
              onClick={sendSmsToActiveBarcode}
            >
              {bulkSmsBusy ? <span className='spinner-border spinner-border-sm me-1' /> : <i className='bi bi-chat-dots me-1' />}
              שלח SMS למנותקים ({smsBulkIds.length})
            </button>
          ) : null}
          {missedPhotoIds.length > 0 ? (
            <button
              className='btn btn-sm btn-light-primary'
              disabled={bulkPhotoBusy || !waConnected}
              title='שלח את תמונת ההסבר לכל מי שקיבל קישור אך עדיין לא תמונה'
              onClick={sendPhotoToMissed}
            >
              {bulkPhotoBusy ? <span className='spinner-border spinner-border-sm me-1' /> : <i className='bi bi-image me-1' />}
              שלח תמונה למי שפספס ({missedPhotoIds.length})
            </button>
          ) : null}
          <input
            className='form-control form-control-sm'
            style={{width: 240}}
            placeholder='חיפוש שם / שם משתמש / טלפון…'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 12}}>
        {loading ? (
          <div style={{textAlign: 'center', padding: 40}}><span className='spinner-border text-primary' /></div>
        ) : rows.length === 0 ? (
          <div style={{textAlign: 'center', color: '#9ca3af', padding: 40}}>לא נמצאו פרופילים מנותקים.</div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed gy-3'>
              <thead>
                <tr className='text-muted fw-bold fs-7 text-uppercase'>
                  <th>פרופיל</th>
                  <th>סיסמה</th>
                  <th>חשבון</th>
                  <th>בדיקה אחרונה</th>
                  <th>ברקוד</th>
                  <th>טלפון</th>
                  <th>נשלח לאחרונה</th>
                  <th className='text-end'>שליחה</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p._id}>
                    <td>
                      <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <div style={{width: 34, height: 34, borderRadius: '50%', background: '#e5e7eb', flex: '0 0 34px'}}>
                          {picUrl(p) ? (
                            <img
                              src={picUrl(p)}
                              alt=''
                              style={{width: 34, height: 34, borderRadius: '50%', objectFit: 'cover'}}
                              referrerPolicy='no-referrer'
                              onError={(e) => {(e.currentTarget as HTMLImageElement).style.visibility = 'hidden'}}
                            />
                          ) : null}
                        </div>
                        <div>
                          <div style={{fontWeight: 600, fontSize: 13}}>
                            <a href={previewUrl(p._id)} target='_blank' rel='noreferrer' title='תצוגה מקדימה של הפרופיל' style={{color: 'inherit', textDecoration: 'none'}}>
                              {p.name}
                              <i className='bi bi-box-arrow-up-left ms-1' style={{fontSize: 10, color: '#3b82f6'}} />
                            </a>
                            {p.server_name ? <span style={{fontSize: 11, color: '#9ca3af', marginRight: 6}}>🖥 {p.server_name}</span> : null}
                          </div>
                          {p.facebook_username ? (
                            <div dir='ltr' style={{fontSize: 11, color: '#9ca3af', textAlign: 'left'}}>
                              {p.facebook_username}
                              <i className='bi bi-clipboard ms-1' style={{cursor: 'pointer'}} onClick={() => copy(p.facebook_username)} />
                            </div>
                          ) : null}
                          {p.note ? (
                            <div style={{fontSize: 11, color: '#b45309'}} title={p.note_at ? relTime(p.note_at) : ''}>📝 {p.note}</div>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td dir='ltr' style={{fontSize: 12}}>
                      {p.facebook_password ? (
                        <span>
                          {revealed[p._id] ? p.facebook_password : '••••••'}
                          <i className={`bi ${revealed[p._id] ? 'bi-eye-slash' : 'bi-eye'} ms-1`} style={{cursor: 'pointer'}} onClick={() => setRevealed((m) => ({...m, [p._id]: !m[p._id]}))} />
                          <i className='bi bi-clipboard ms-1' style={{cursor: 'pointer'}} onClick={() => copy(p.facebook_password)} />
                        </span>
                      ) : <span className='text-muted'>—</span>}
                    </td>
                    <td style={{fontSize: 13}} title={p.account_name}>
                      {p.account_name ? (
                        <div style={{maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {p.account_name}
                        </div>
                      ) : <span className='text-muted'>—</span>}
                      {p.excluded ? <span className='badge badge-light-warning' style={{fontSize: 10}}>מוחרג</span> : null}
                    </td>
                    <td style={{fontSize: 12, color: '#6b7280'}}>{relTime(p.last_health_check)}</td>
                    <td>
                      {p.barcode_active ? (
                        <a href={p.login_url} target='_blank' rel='noreferrer' className='badge badge-light-success' title='פתח את הקישור'>● פעיל</a>
                      ) : (
                        <div className='d-flex align-items-center gap-2'>
                          <a href={p.login_url} target='_blank' rel='noreferrer' className='badge badge-light-secondary' title='פתח את הקישור'>אין קוד</a>
                          <button
                            className='btn btn-sm btn-light-primary py-1 px-2'
                            style={{fontSize: 11}}
                            disabled={!!qrBusy[p._id]}
                            title='הפעל את הבוט כדי לייצר קוד התחברות (15-30 שניות)'
                            onClick={() => tryGetQr(p)}
                          >
                            {qrBusy[p._id] ? <span className='spinner-border spinner-border-sm' /> : <><i className='bi bi-qr-code me-1' />צור קוד</>}
                          </button>
                          {qrTried[p._id] && !qrBusy[p._id] ? <span className='text-muted' style={{fontSize: 10}}>ממתין…</span> : null}
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        className='form-control form-control-sm'
                        dir='ltr'
                        style={{maxWidth: 150}}
                        placeholder='05X-XXXXXXX'
                        value={phones[p._id] || ''}
                        onChange={(e) => setPhones((m) => ({...m, [p._id]: e.target.value}))}
                      />
                    </td>
                    <td style={{fontSize: 12, color: '#6b7280'}}>
                      <div>
                        {sentFor[p._id] ? <span className='text-success'>נשלח כעת ✓</span>
                          : p.last_message ? <><i className='bi bi-whatsapp text-success me-1' />{relTime(p.last_message.created_at)}</>
                          : <span className='text-muted'>—</span>}
                      </div>
                      {p.sms_sent ? <div className='text-info' style={{fontSize: 11}}><i className='bi bi-chat-dots me-1' />SMS נשלח</div> : null}
                    </td>
                    <td className='text-end' style={{whiteSpace: 'nowrap'}}>
                      <div className='d-flex justify-content-end gap-1'>
                        <button
                          className='btn btn-sm btn-light-success'
                          disabled={!!sending[p._id] || !(phones[p._id] || '').trim() || !waConnected || p.excluded}
                          title={p.excluded ? 'חשבון מוחרג' : !waConnected ? 'חבר וואטסאפ קודם' : 'שלח קישור התחברות' + (attachImage ? ' + תמונת הסבר' : '')}
                          onClick={() => send(p)}
                        >
                          <i className='bi bi-whatsapp me-1' /> {sending[p._id] ? 'שולח…' : 'שלח קישור'}
                        </button>
                        <button
                          className='btn btn-sm btn-light-primary btn-icon'
                          disabled={!!photoBusy[p._id] || !(phones[p._id] || '').trim() || !waConnected || p.excluded}
                          title='שלח רק את תמונת ההסבר'
                          onClick={() => sendPhotoOnly(p)}
                        >
                          {photoBusy[p._id] ? <span className='spinner-border spinner-border-sm' /> : <i className='bi bi-image' />}
                        </button>
                        {smsConfigured ? (
                          <button
                            className='btn btn-sm btn-light-info'
                            disabled={!!smsBusy[p._id] || !(phones[p._id] || '').trim() || p.excluded}
                            title={p.excluded ? 'חשבון מוחרג' : 'שלח קישור התחברות ב‑SMS'}
                            onClick={() => sendSms(p)}
                          >
                            {smsBusy[p._id] ? <span className='spinner-border spinner-border-sm' /> : <><i className='bi bi-chat-dots me-1' />SMS</>}
                          </button>
                        ) : null}
                        <button
                          className={`btn btn-sm btn-icon ${p.excluded ? 'btn-light-warning' : 'btn-light'}`}
                          title={p.excluded ? 'בטל החרגת החשבון' : 'החרג את החשבון משליחה מרוכזת/אוטומטית'}
                          onClick={() => toggleExclude(p)}
                        >
                          <i className={`bi ${p.excluded ? 'bi-slash-circle' : 'bi-ban'}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default LoggedOutProfilesPage
